import {
    YaasPositionStatus,
    YaasPositionStatusBadge
} from "@solomon-apps/shared";
import {
    AlertBadge as SemanticAlertBadge,
    BoundaryBadge as SemanticBoundaryBadge,
    CountChip as SemanticCountChip,
    MetadataChip as SemanticMetadataChip,
    OutcomeBadge as SemanticOutcomeBadge,
    LifecycleStatusBadge as SemanticStatusPill
} from "@solomon-apps/shared/src/components/console";
import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import { StatusPill } from "@solomon-apps/shared/src/components/Metrics/StatusPill";
import { Badge } from "@solomon-apps/shared/src/components/ui/badge";
import { Card } from "@solomon-apps/shared/src/components/ui/card";
import {
    TabsMenu,
    TabTrigger
} from "@solomon-apps/shared/src/components/ui/tabs-menu";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { PolicyStatusBadge as SharedPolicyStatusBadge } from "@solomon-apps/shared/src/components/Yaas/PolicyStatusBadge";
import { cn } from "@solomon-apps/shared/src/utils/cn";
import { motion } from "motion/react";
import React, { useState } from "react";

type ExplorationTab = "current" | "prototype";
type BadgeVariant = NonNullable<React.ComponentProps<typeof Badge>["variant"]>;

type CycleStatus = "completed" | "failed";

type SettlementResult = "Rewarded" | "NotRewarded";

type PolicyDecisionStatus =
    | "Blocked"
    | "Warning"
    | "Passed"
    | "Applied"
    | "NotApplicable";
type PolicyEffect =
    | "BlockedReward"
    | "ScreeningHold"
    | "ReducedReward"
    | "NoEffect";
type Permission = "delegated" | "constrained" | "locked";
type VenueStatus = "active" | "paused";
type DotState = "success" | "pending" | "error" | "disabled";

interface BadgeExample {
    name: string;
    source: string;
    sample: React.ReactNode;
    note?: string;
}

interface BadgeGroup {
    title: string;
    intent: string;
    examples: BadgeExample[];
}

interface PrototypeGroup {
    title: string;
    component: string;
    intent: string;
    rule: string;
    examples: BadgeExample[];
}

const BADGE_GROUPS: BadgeGroup[] = [
    {
        title: "Shared primitive",
        intent: "The base shape every simple value badge starts from.",
        examples: [
            primitiveExample("Default", "default"),
            primitiveExample("Secondary", "secondary"),
            primitiveExample("Outline", "outline"),
            primitiveExample("Success", "success"),
            primitiveExample("Warning", "warning"),
            primitiveExample("Error", "error")
        ]
    },
    {
        title: "Settlement cycle status",
        intent: "Operational state of a settlement cycle in issuer reporting.",
        examples: [
            {
                name: "Completed",
                source: "SettlementStatus.tsx - SettlementStatusPill",
                sample: <ClientCycleStatusBadge status="completed" />,
                note: "Client Accounts does not surface lifecycle status."
            },
            {
                name: "Failed",
                source: "SettlementStatus.tsx - SettlementStatusPill",
                sample: <ClientCycleStatusBadge status="failed" />,
                note: "Failed settlements are issuer-facing only."
            },
            {
                name: "Issuer settlement completed",
                source: "SettlementStatus.tsx - SettlementStatusPill",
                sample: (
                    <SettlementStatusPill status="Completed" state="success" />
                ),
                note: "Same intent as client completed, different shape."
            }
        ]
    },
    {
        title: "Settlement result",
        intent: "Outcome for an account after a settlement evaluates it.",
        examples: [
            {
                name: "Rewarded",
                source: "AccountsPage.tsx - ResultBadge",
                sample: <ResultBadge result="Rewarded" />
            },
            {
                name: "Not rewarded",
                source: "AccountsPage.tsx - ResultBadge",
                sample: <ResultBadge result="NotRewarded" />
            },
            {
                name: "Compact list result",
                source: "AccountsPage.tsx - ResultBadge compact",
                sample: <ResultBadge result="NotRewarded" compact />,
                note: "Same color as the full result, shorter label."
            }
        ]
    },
    {
        title: "Policy state",
        intent: "Whether a policy rule needs client action, is configured, or is issuer-managed.",
        examples: [
            {
                name: "Needs action row",
                source: "PoliciesPage.tsx - RowStatusIndicator",
                sample: (
                    <DotOnlyLabel color="accent" label="Needs your action" />
                ),
                note: "Dot-only in lists, not a badge component."
            },
            {
                name: "Configured row",
                source: "PoliciesPage.tsx - RowStatusIndicator",
                sample: <DotOnlyLabel color="success" label="Configured" />,
                note: "Dot-only in lists, not a badge component."
            },
            {
                name: "Managed row",
                source: "PoliciesPage.tsx - RowStatusIndicator",
                sample: (
                    <DotOnlyLabel color="disabled" label="Issuer-managed" />
                ),
                note: "Dot-only in lists, not a badge component."
            },
            {
                name: "Saved",
                source: "PoliciesPage.tsx - SavedBadge",
                sample: <SavedBadge />
            },
            {
                name: "Issuer active",
                source: "PolicyStatusBadge.tsx",
                sample: <SharedPolicyStatusBadge status="active" />,
                note: "Uppercase, rounded border, dot included."
            },
            {
                name: "Issuer draft",
                source: "PolicyStatusBadge.tsx",
                sample: <SharedPolicyStatusBadge status="draft" />,
                note: "Same domain as policy state, different styling language."
            }
        ]
    },
    {
        title: "Authority and permission",
        intent: "Who controls a value, and whether the client can set it freely or within bounds.",
        examples: [
            {
                name: "Client delegated",
                source: "PoliciesPage.tsx - PermissionBadge",
                sample: <PermissionBadge permission="delegated" />
            },
            {
                name: "Client constrained",
                source: "PoliciesPage.tsx - PermissionBadge",
                sample: <PermissionBadge permission="constrained" />,
                note: "Uses tint tokens, while issuer constrained uses warning."
            },
            {
                name: "Client locked",
                source: "PoliciesPage.tsx - PermissionBadge",
                sample: <PermissionBadge permission="locked" />
            },
            {
                name: "Issuer constrained",
                source: "RuleTypeDetail.tsx - AuthorityBadge",
                sample: <IssuerAuthorityBadge permission="Constrained" />
            },
            {
                name: "Issuer delegated",
                source: "RuleTypeDetail.tsx - AuthorityBadge",
                sample: <IssuerAuthorityBadge permission="Delegated" />
            },
            {
                name: "Client set",
                source: "RuleTypeDetail.tsx - ClientSetBadge",
                sample: <ClientSetBadge />
            }
        ]
    },
    {
        title: "Venue, scope, and category",
        intent: "Metadata badges that locate a rule, product, pool, or category.",
        examples: [
            {
                name: "Venue active",
                source: "PoliciesPage.tsx - VenueBadge",
                sample: <VenueBadge name="Marinade Native" status="active" />
            },
            {
                name: "Venue paused",
                source: "PoliciesPage.tsx - VenueBadge",
                sample: <VenueBadge name="Orca SOL" status="paused" />
            },
            {
                name: "Policy category",
                source: "PoliciesPage.tsx - CategoryBadge",
                sample: <CategoryBadge label="Account limits" />
            },
            {
                name: "Issuer global scope",
                source: "RuleTypeDetail.tsx - ScopeBadge",
                sample: <ScopeBadge muted label="Global" />
            },
            {
                name: "Issuer client scope",
                source: "RuleTypeDetail.tsx - ScopeBadge",
                sample: <ScopeBadge label="Atlas Treasury" />
            },
            {
                name: "Attention count",
                source: "PoliciesPage.tsx - VenueFilterItem",
                sample: <AttentionCountBadge count={3} />,
                note: "Count chip is hand-rolled and only exists inside a pressed filter."
            }
        ]
    },
    {
        title: "Decision trail outcome",
        intent: "Per-rule account evaluation details in Accounts.",
        examples: [
            {
                name: "Blocked",
                source: "AccountsPage.tsx - PolicyStatusBadge",
                sample: <PolicyStatusBadge status="Blocked" />
            },
            {
                name: "Warning",
                source: "AccountsPage.tsx - PolicyStatusBadge",
                sample: <PolicyStatusBadge status="Warning" />
            },
            {
                name: "Passed",
                source: "AccountsPage.tsx - PolicyStatusBadge",
                sample: <PolicyStatusBadge status="Passed" />
            },
            {
                name: "Not applicable",
                source: "AccountsPage.tsx - PolicyStatusBadge",
                sample: <PolicyStatusBadge status="NotApplicable" />
            },
            {
                name: "Blocked reward",
                source: "AccountsPage.tsx - EffectBadge",
                sample: <EffectBadge effect="BlockedReward" />
            },
            {
                name: "No effect",
                source: "AccountsPage.tsx - EffectBadge",
                sample: <EffectBadge effect="NoEffect" />
            }
        ]
    },
    {
        title: "Screening and compliance",
        intent: "External checks and compliance states shown on an account or onboarding record.",
        examples: [
            {
                name: "Critical screening",
                source: "AccountsPage.tsx - ScreeningChecks",
                sample: <Badge variant="error">Flagged</Badge>
            },
            {
                name: "Non-critical screening",
                source: "AccountsPage.tsx - ScreeningChecks",
                sample: <Badge variant="secondary">Clear</Badge>
            },
            {
                name: "Onboarding rejected",
                source: "OnboardingPanel.tsx",
                sample: <Badge variant="error">Rejected</Badge>
            },
            {
                name: "Team active",
                source: "TeamMemberStatusBadge.tsx",
                sample: (
                    <StatusPill
                        indicatorState="success"
                        textClassName="text-success-base"
                        textVariant="label"
                    >
                        Active
                    </StatusPill>
                )
            },
            {
                name: "Team invited",
                source: "TeamMemberStatusBadge.tsx",
                sample: (
                    <StatusPill
                        indicatorState="pending"
                        textClassName="text-warning-base"
                        textVariant="label"
                    >
                        Invited
                    </StatusPill>
                )
            },
            {
                name: "Team disabled",
                source: "TeamMemberStatusBadge.tsx",
                sample: (
                    <StatusPill
                        indicatorState="disabled"
                        textClassName="text-foreground-muted"
                        textVariant="label"
                    >
                        Disabled
                    </StatusPill>
                )
            }
        ]
    },
    {
        title: "Portfolio position status",
        intent: "Position lifecycle badges shown on the client Portfolio page.",
        examples: [
            {
                name: "Active position",
                source: "Dashboard/PositionsSummary.tsx - YaasPositionStatusBadge",
                sample: (
                    <YaasPositionStatusBadge
                        status={YaasPositionStatus.Active}
                    />
                ),
                note: "Shared badge primitive; success tone marks an active position."
            },
            {
                name: "Pending position",
                source: "Dashboard/PositionsSummary.tsx - YaasPositionStatusBadge",
                sample: (
                    <YaasPositionStatusBadge
                        status={YaasPositionStatus.Pending}
                    />
                ),
                note: "Warning tone is also used for suspended positions."
            },
            {
                name: "Rejected position",
                source: "Dashboard/PositionsSummary.tsx - YaasPositionStatusBadge",
                sample: (
                    <YaasPositionStatusBadge
                        status={YaasPositionStatus.Rejected}
                    />
                )
            },
            {
                name: "Suspended position",
                source: "Dashboard/PositionsSummary.tsx - YaasPositionStatusBadge",
                sample: (
                    <YaasPositionStatusBadge
                        status={YaasPositionStatus.Suspended}
                    />
                )
            },
            {
                name: "Positive earned delta",
                source: "Dashboard/HeadlineBlock.tsx - inline number signal",
                sample: (
                    <PortfolioDeltaSignal tone="positive">
                        +$1,240.88
                    </PortfolioDeltaSignal>
                ),
                note: "Not technically a badge, but it is a value signal competing in the same visual layer."
            },
            {
                name: "Neutral earned delta",
                source: "Dashboard/HeadlineBlock.tsx - inline number signal",
                sample: (
                    <PortfolioDeltaSignal tone="neutral">
                        $0.00
                    </PortfolioDeltaSignal>
                ),
                note: "Worth tracking separately from semantic badges."
            }
        ]
    }
];

const PROTOTYPE_GROUPS: PrototypeGroup[] = [
    {
        title: "Lifecycle Status",
        component: "StatusPill",
        intent: "Use for a thing that is moving through a lifecycle.",
        rule: "Dot carries state; the container stays neutral so status does not compete with final outcomes.",
        examples: [
            {
                name: "Settlement completed",
                source: "SettlementStatusBadge status=completed",
                sample: (
                    <SemanticStatusPill state="success">
                        Completed
                    </SemanticStatusPill>
                )
            },
            {
                name: "Settlement failed",
                source: "SettlementStatusBadge status=failed",
                sample: (
                    <SemanticStatusPill state="error">
                        Failed
                    </SemanticStatusPill>
                )
            },
            {
                name: "Policy paused",
                source: "PolicyLifecycleBadge status=paused",
                sample: (
                    <SemanticStatusPill state="disabled">
                        Paused
                    </SemanticStatusPill>
                )
            },
            {
                name: "Portfolio position active",
                source: "PositionStatusBadge status=Active",
                sample: (
                    <SemanticStatusPill state="success">
                        Active
                    </SemanticStatusPill>
                )
            },
            {
                name: "Portfolio position rejected",
                source: "PositionStatusBadge status=Rejected",
                sample: (
                    <SemanticStatusPill state="error">
                        Rejected
                    </SemanticStatusPill>
                )
            }
        ]
    },
    {
        title: "Evaluation Outcome",
        component: "OutcomeBadge",
        intent: "Use for the result produced after evaluation or settlement.",
        rule: "No dot. Filled tone means the system has already decided an outcome.",
        examples: [
            {
                name: "Rewarded",
                source: "SettlementResultBadge result=Rewarded",
                sample: (
                    <SemanticOutcomeBadge tone="success">
                        Rewarded
                    </SemanticOutcomeBadge>
                )
            },
            {
                name: "Not rewarded",
                source: "SettlementResultBadge result=NotRewarded",
                sample: (
                    <SemanticOutcomeBadge tone="danger">
                        Not rewarded
                    </SemanticOutcomeBadge>
                )
            },
            {
                name: "No settlement history",
                source: "AccountsPage.tsx empty account-history state",
                sample: (
                    <SemanticOutcomeBadge tone="neutral">
                        No settlement history
                    </SemanticOutcomeBadge>
                )
            }
        ]
    },
    {
        title: "Permission Boundary",
        component: "BoundaryBadge",
        intent: "Use for who controls a Policy Area and how much freedom the client has.",
        rule: "Boundary colors never mean success or failure; they mean control model.",
        examples: [
            {
                name: "Delegated Policy",
                source: "PolicyBoundaryBadge boundary=delegated",
                sample: (
                    <SemanticBoundaryBadge boundary="delegated">
                        Delegated
                    </SemanticBoundaryBadge>
                )
            },
            {
                name: "Constrained Policy",
                source: "PolicyBoundaryBadge boundary=constrained",
                sample: (
                    <SemanticBoundaryBadge boundary="constrained">
                        Constrained
                    </SemanticBoundaryBadge>
                )
            },
            {
                name: "Locked Policy",
                source: "PolicyBoundaryBadge boundary=locked",
                sample: (
                    <SemanticBoundaryBadge boundary="locked">
                        Locked
                    </SemanticBoundaryBadge>
                )
            }
        ]
    },
    {
        title: "Metadata",
        component: "MetadataChip",
        intent: "Use for labels that identify what something is, not whether it is good or bad.",
        rule: "Neutral outline only. Metadata should not smuggle status color.",
        examples: [
            {
                name: "Client Venue",
                source: "VenueChip",
                sample: (
                    <SemanticMetadataChip>Marinade Native</SemanticMetadataChip>
                )
            },
            {
                name: "Policy Category",
                source: "PolicyCategoryChip",
                sample: (
                    <SemanticMetadataChip>Account limits</SemanticMetadataChip>
                )
            },
            {
                name: "Scope",
                source: "ScopeChip",
                sample: <SemanticMetadataChip>Global</SemanticMetadataChip>
            },
            {
                name: "Venue type",
                source: "VenueTypeChip",
                sample: <SemanticMetadataChip>LST</SemanticMetadataChip>
            }
        ]
    },
    {
        title: "Count",
        component: "CountChip",
        intent: "Use for compact numeric quantities attached to another control.",
        rule: "Numbers are adjuncts, not standalone statuses. Accent only when the count demands attention.",
        examples: [
            {
                name: "Attention count",
                source: "CountChip tone=attention",
                sample: (
                    <SemanticCountChip tone="attention">3</SemanticCountChip>
                )
            },
            {
                name: "Neutral count",
                source: "CountChip tone=neutral",
                sample: <SemanticCountChip tone="neutral">18</SemanticCountChip>
            },
            {
                name: "Overflow count",
                source: "CountChip tone=attention overflow",
                sample: (
                    <SemanticCountChip tone="attention">9+</SemanticCountChip>
                )
            }
        ]
    },
    {
        title: "Alert Severity",
        component: "AlertBadge",
        intent: "Use for exceptional compliance, screening, or operational risk.",
        rule: "Only render when something needs attention. Clear/non-critical states should usually stay quiet.",
        examples: [
            {
                name: "Critical screening",
                source: "ScreeningAlertBadge severity=critical",
                sample: (
                    <SemanticAlertBadge severity="critical">
                        Critical
                    </SemanticAlertBadge>
                )
            },
            {
                name: "Review",
                source: "ScreeningAlertBadge severity=review",
                sample: (
                    <SemanticAlertBadge severity="review">
                        Review
                    </SemanticAlertBadge>
                )
            },
            {
                name: "Info",
                source: "ScreeningAlertBadge severity=info",
                sample: (
                    <SemanticAlertBadge severity="info">
                        Info
                    </SemanticAlertBadge>
                )
            }
        ]
    }
];

export function ExplorationBadgesPage(): React.ReactElement {
    const [tab, setTab] = useState<ExplorationTab>("current");

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="flex w-full flex-col"
        >
            <header className="border-b border-line-base px-6 py-6">
                <Text variant="label" className="text-foreground-muted">
                    Exploration
                </Text>
                <h1 className="mt-2 font-display text-[2rem] leading-10 tracking-normal text-foreground-base">
                    Badge Inventory
                </h1>
                <p className="mt-3 max-w-3xl font-sans text-sm leading-5 tracking-normal text-foreground-muted">
                    Current badge and pill variants from client Accounts, client
                    Policies, and the closest issuer-console equivalents,
                    grouped by the job each badge is doing.
                </p>
                <div className="mt-5">
                    <TabsMenu
                        value={tab}
                        onValueChange={(value) =>
                            setTab(value as ExplorationTab)
                        }
                        align="start"
                        border="bottom"
                    >
                        <TabTrigger value="current" size="sm">
                            Current
                        </TabTrigger>
                        <TabTrigger value="prototype" size="sm">
                            Prototype
                        </TabTrigger>
                    </TabsMenu>
                </div>
            </header>

            {tab === "current" ? <CurrentInventory /> : <PrototypeInventory />}
        </motion.div>
    );
}

function CurrentInventory(): React.ReactElement {
    return (
        <main className="grid gap-6 px-6 py-6">
            {BADGE_GROUPS.map((group) => (
                <BadgeGroupSection key={group.title} group={group} />
            ))}
        </main>
    );
}

function PrototypeInventory(): React.ReactElement {
    return (
        <main className="grid gap-6 px-6 py-6">
            <PrototypePrinciples />
            {PROTOTYPE_GROUPS.map((group) => (
                <PrototypeGroupSection key={group.title} group={group} />
            ))}
            <PrototypeComparison />
        </main>
    );
}

function PrototypePrinciples(): React.ReactElement {
    const principles = [
        {
            title: "Intent first",
            body: "Pick the component by semantic job before choosing color."
        },
        {
            title: "Status is not outcome",
            body: "Lifecycle gets a dot pill; final results get filled outcome badges."
        },
        {
            title: "Metadata stays quiet",
            body: "Venue, scope, type, and category chips use neutral treatment."
        }
    ];

    return (
        <Card asElement="section" surface="raised" rounded={false}>
            <div className="grid divide-y divide-line-base lg:grid-cols-3 lg:divide-x lg:divide-y-0">
                {principles.map((principle) => (
                    <div key={principle.title} className="px-5 py-5">
                        <Text
                            variant="label"
                            className="text-foreground-neutral"
                        >
                            Prototype rule
                        </Text>
                        <h2 className="mt-1 font-sans text-xl font-bold leading-7 tracking-normal text-foreground-base">
                            {principle.title}
                        </h2>
                        <p className="mt-2 font-sans text-sm leading-5 tracking-normal text-foreground-muted">
                            {principle.body}
                        </p>
                    </div>
                ))}
            </div>
        </Card>
    );
}

function PrototypeGroupSection({
    group
}: {
    group: PrototypeGroup;
}): React.ReactElement {
    return (
        <Card asElement="section" surface="raised" rounded={false}>
            <div className="grid gap-5 border-b border-line-base px-5 py-5 xl:grid-cols-[minmax(0,1fr)_18rem] xl:items-start">
                <div>
                    <Text variant="label" className="text-foreground-neutral">
                        {group.component}
                    </Text>
                    <h2 className="mt-1 font-sans text-xl font-bold leading-7 tracking-normal text-foreground-base">
                        {group.title}
                    </h2>
                    <p className="mt-2 max-w-3xl font-sans text-sm leading-5 tracking-normal text-foreground-muted">
                        {group.intent}
                    </p>
                </div>
                <div className="border border-line-base bg-surface-sunken px-4 py-3">
                    <Text variant="label" className="text-foreground-neutral">
                        Rule
                    </Text>
                    <p className="mt-1 font-sans text-xs leading-4 tracking-normal text-foreground-muted">
                        {group.rule}
                    </p>
                </div>
            </div>
            <div className="grid divide-y divide-line-base lg:grid-cols-2 lg:divide-x lg:divide-y-0">
                {group.examples.map((example) => (
                    <BadgeExampleRow
                        key={`${group.title}-${example.name}`}
                        example={example}
                    />
                ))}
            </div>
        </Card>
    );
}

function PrototypeComparison(): React.ReactElement {
    return (
        <Card asElement="section" surface="raised" rounded={false}>
            <div className="border-b border-line-base px-5 py-5">
                <Text variant="label" className="text-foreground-neutral">
                    Same words, different jobs
                </Text>
                <h2 className="mt-1 font-sans text-xl font-bold leading-7 tracking-normal text-foreground-base">
                    The split in practice
                </h2>
            </div>
            <div className="grid divide-y divide-line-base lg:grid-cols-3 lg:divide-x lg:divide-y-0">
                <PrototypeScenario
                    title="Settlement lifecycle"
                    copy="Issuer reporting keeps the cycle state coarse."
                    sample={
                        <SemanticStatusPill state="success">
                            Completed
                        </SemanticStatusPill>
                    }
                />
                <PrototypeScenario
                    title="Account result"
                    copy="A client account was evaluated and received a final result."
                    sample={
                        <SemanticOutcomeBadge tone="success">
                            Rewarded
                        </SemanticOutcomeBadge>
                    }
                />
                <PrototypeScenario
                    title="Policy control"
                    copy="The issuer allows the client to choose a value within bounds."
                    sample={
                        <SemanticBoundaryBadge boundary="constrained">
                            Constrained
                        </SemanticBoundaryBadge>
                    }
                />
            </div>
        </Card>
    );
}

function PrototypeScenario({
    title,
    copy,
    sample
}: {
    title: string;
    copy: string;
    sample: React.ReactNode;
}): React.ReactElement {
    return (
        <div className="px-5 py-5">
            <Text variant="label" className="text-foreground-base">
                {title}
            </Text>
            <p className="mt-2 min-h-10 font-sans text-xs leading-4 tracking-normal text-foreground-muted">
                {copy}
            </p>
            <div className="mt-4 flex min-h-14 items-center border border-line-base bg-surface-base px-4 py-3">
                {sample}
            </div>
        </div>
    );
}

function BadgeGroupSection({
    group
}: {
    group: BadgeGroup;
}): React.ReactElement {
    return (
        <Card asElement="section" surface="raised" rounded={false}>
            <div className="grid gap-4 border-b border-line-base px-5 py-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                <div>
                    <Text variant="label" className="text-foreground-neutral">
                        {group.examples.length} variants
                    </Text>
                    <h2 className="mt-1 font-sans text-xl font-bold leading-7 tracking-normal text-foreground-base">
                        {group.title}
                    </h2>
                    <p className="mt-2 max-w-3xl font-sans text-sm leading-5 tracking-normal text-foreground-muted">
                        {group.intent}
                    </p>
                </div>
            </div>
            <div className="grid divide-y divide-line-base lg:grid-cols-2 lg:divide-x lg:divide-y-0">
                {group.examples.map((example) => (
                    <BadgeExampleRow
                        key={`${group.title}-${example.name}`}
                        example={example}
                    />
                ))}
            </div>
        </Card>
    );
}

function BadgeExampleRow({
    example
}: {
    example: BadgeExample;
}): React.ReactElement {
    return (
        <div className="grid gap-4 px-5 py-4 sm:grid-cols-[13rem_minmax(0,1fr)] sm:items-center">
            <div className="min-w-0">
                <Text variant="label" className="text-foreground-base">
                    {example.name}
                </Text>
                <Text
                    variant="label"
                    render={<div />}
                    className="mt-1 break-words text-foreground-neutral"
                >
                    {example.source}
                </Text>
                {example.note ? (
                    <p className="mt-2 font-sans text-xs leading-4 tracking-normal text-foreground-muted">
                        {example.note}
                    </p>
                ) : null}
            </div>
            <div className="flex min-h-14 min-w-0 items-center overflow-x-auto border border-line-base bg-surface-base px-4 py-3">
                {example.sample}
            </div>
        </div>
    );
}

function PortfolioDeltaSignal({
    tone,
    children
}: {
    tone: "positive" | "neutral";
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <Text
            variant="number"
            render={<span />}
            className={
                tone === "positive"
                    ? "text-success-base"
                    : "text-foreground-muted"
            }
        >
            {children}
        </Text>
    );
}

function primitiveExample(name: string, variant: BadgeVariant): BadgeExample {
    return {
        name,
        source: "shared/ui/badge.tsx",
        sample: <Badge variant={variant}>{name}</Badge>
    };
}

function ClientCycleStatusBadge({
    status
}: {
    status: CycleStatus;
}): React.ReactElement {
    const variant: BadgeVariant = status === "completed" ? "success" : "error";

    return <Badge variant={variant}>{cycleStatusLabel(status)}</Badge>;
}

function ResultBadge({
    result,
    compact = false
}: {
    result: SettlementResult;
    compact?: boolean;
}): React.ReactElement {
    const variant: BadgeVariant = result === "Rewarded" ? "success" : "error";

    return (
        <Badge variant={variant}>
            {compact ? resultShortLabel(result) : resultLabel(result)}
        </Badge>
    );
}

function SettlementStatusPill({
    status,
    state
}: {
    status: string;
    state: DotState;
}): React.ReactElement {
    return (
        <span className="inline-flex items-center gap-1.5 rounded-2xs border border-line-base bg-surface-raised px-2 py-0.5">
            <IndicationUnit variant="status" state={state} size={8} />
            <Text
                variant="label"
                render={<span />}
                className="text-foreground-base"
            >
                {status}
            </Text>
        </span>
    );
}

function DotOnlyLabel({
    color,
    label
}: {
    color: "accent" | "success" | "disabled";
    label: string;
}): React.ReactElement {
    return (
        <span className="inline-flex items-center gap-2">
            <IndicationUnit
                variant="solid"
                color={color}
                size={7}
                alignPt={5}
            />
            <Text
                variant="label"
                render={<span />}
                className="text-foreground-base"
            >
                {label}
            </Text>
        </span>
    );
}

function SavedBadge(): React.ReactElement {
    return (
        <Badge variant="success" className="gap-1.5 rounded-sm">
            <IndicationUnit variant="solid" color="success" size={6} />
            Saved
        </Badge>
    );
}

function PermissionBadge({
    permission
}: {
    permission: Permission;
}): React.ReactElement {
    if (permission === "delegated") {
        return (
            <Badge variant="success" className="gap-1.5 rounded-sm">
                <IndicationUnit variant="solid" color="success" size={6} />
                Delegated
            </Badge>
        );
    }
    if (permission === "constrained") {
        return (
            <Badge
                variant="outline"
                className="gap-1.5 rounded-sm border-tint/30 bg-tint/10 text-tint"
            >
                <IndicationUnit variant="solid" color="tint" size={6} />
                Constrained
            </Badge>
        );
    }
    return (
        <Badge
            variant="secondary"
            className="gap-1.5 rounded-sm text-foreground-muted"
        >
            <IndicationUnit variant="solid" color="disabled" size={6} />
            Locked
        </Badge>
    );
}

function IssuerAuthorityBadge({
    permission
}: {
    permission: "Constrained" | "Delegated";
}): React.ReactElement {
    const className =
        permission === "Constrained"
            ? "inline-flex items-center rounded-xs border border-warning/25 bg-warning/10 px-1.5 py-0.5 text-warning"
            : "inline-flex items-center rounded-xs border border-line-base bg-surface-sunken px-1.5 py-0.5 text-foreground-base";

    return (
        <span className={className}>
            <Text variant="label" render={<span />}>
                {permission}
            </Text>
        </span>
    );
}

function ClientSetBadge(): React.ReactElement {
    return (
        <span className="inline-flex items-center gap-1.5 rounded-xs border border-success/25 bg-success/10 px-1.5 py-0.5 text-success">
            <IndicationUnit variant="solid" color="success" size={5} />
            <Text variant="label" render={<span />}>
                Client set
            </Text>
        </span>
    );
}

function VenueBadge({
    name,
    status
}: {
    name: string;
    status: VenueStatus;
}): React.ReactElement {
    return (
        <Badge variant="outline" className="gap-1.5 rounded-sm">
            <IndicationUnit
                variant="solid"
                color={status === "active" ? "success" : "warning"}
                size={6}
            />
            {name}
        </Badge>
    );
}

function CategoryBadge({ label }: { label: string }): React.ReactElement {
    return (
        <Badge variant="outline" className="rounded-sm text-foreground-muted">
            {label}
        </Badge>
    );
}

function ScopeBadge({
    label,
    muted = false
}: {
    label: string;
    muted?: boolean;
}): React.ReactElement {
    return (
        <span
            className={cn(
                "inline-flex items-center rounded-xs border border-line-base bg-surface-sunken px-1.5 py-0.5",
                muted ? "text-foreground-imprint" : "text-foreground-base"
            )}
        >
            <Text variant="label" render={<span />}>
                {label}
            </Text>
        </span>
    );
}

function AttentionCountBadge({ count }: { count: number }): React.ReactElement {
    return (
        <span className="inline-flex min-w-5 items-center justify-center rounded-sm bg-accent/10 px-1.5 py-0.5 text-xs text-accent">
            {count}
        </span>
    );
}

function PolicyStatusBadge({
    status
}: {
    status: PolicyDecisionStatus;
}): React.ReactElement {
    if (status === "NotApplicable") {
        return (
            <Badge
                variant="secondary"
                className="border border-line-base bg-surface-sunken px-2 py-0.5 text-[11px] leading-4 text-foreground-imprint md:text-xs md:leading-4 [&>svg]:!size-2.5"
            >
                Not applicable
            </Badge>
        );
    }

    const variant: BadgeVariant =
        status === "Blocked"
            ? "error"
            : status === "Warning"
              ? "warning"
              : status === "Passed" || status === "Applied"
                ? "success"
                : "secondary";

    return (
        <Badge
            variant={variant}
            className="px-2 py-0.5 text-[11px] leading-4 md:text-xs md:leading-4 [&>svg]:!size-2.5"
        >
            {status}
        </Badge>
    );
}

function EffectBadge({ effect }: { effect: PolicyEffect }): React.ReactElement {
    if (effect === "NoEffect") {
        return (
            <Badge
                variant="secondary"
                className="border border-line-base bg-surface-sunken px-2 py-0.5 text-[11px] leading-4 text-foreground-imprint md:text-xs md:leading-4 [&>svg]:!size-2.5"
            >
                No effect
            </Badge>
        );
    }

    return (
        <Badge
            variant={effect === "BlockedReward" ? "error" : "warning"}
            className="px-2 py-0.5 text-[11px] leading-4 md:text-xs md:leading-4 [&>svg]:!size-2.5"
        >
            {effectLabel(effect)}
        </Badge>
    );
}

function cycleStatusLabel(status: CycleStatus): string {
    switch (status) {
        case "completed":
            return "Completed";
        case "failed":
            return "Failed";
    }
}

function resultLabel(result: SettlementResult): string {
    switch (result) {
        case "Rewarded":
            return "Rewarded";
        case "NotRewarded":
            return "Not rewarded";
    }
}

function resultShortLabel(result: SettlementResult): string {
    return resultLabel(result);
}

function effectLabel(effect: PolicyEffect): string {
    switch (effect) {
        case "BlockedReward":
            return "Blocked reward";
        case "ScreeningHold":
            return "Screening hold";
        case "ReducedReward":
            return "Reduced reward";
        case "NoEffect":
            return "No effect";
    }
}
